/**
 * Insights Service - API client for AI insights, alerts, and chat
 */
import apiClient from './apiClient';

const insightsService = {
  // ========================================
  // INSIGHTS
  // ========================================

  /**
   * Generate new insights using AI based on historical data
   * @param {number} days - Number of days to analyze (default: 30)
   */
  generateInsights: async (days = 30) => {
    const response = await apiClient.post('/insights/generate', { days });
    return response.data;
  },

  /**
   * Get list of active insights
   * @param {Object} params - Query parameters (status, category, limit)
   */
  getInsights: async (params = {}) => {
    const response = await apiClient.get('/insights/list', { params });
    return response.data;
  },

  /**
   * Acknowledge an insight (mark as seen)
   * @param {string} insightId - UUID of the insight
   */
  acknowledgeInsight: async (insightId) => {
    const response = await apiClient.post('/insights/acknowledge', {
      insight_id: insightId
    });
    return response.data;
  },

  // ========================================
  // ALERTS (with deduplication)
  // ========================================

  /**
   * Get alerts with deduplication applied
   * @param {Object} params - Query parameters (status, severity, limit)
   */
  getAlerts: async (params = {}) => {
    const response = await apiClient.get('/insights/alerts', { params });
    return response.data;
  },

  /**
   * Acknowledge an alert
   * @param {string} alertId - UUID of the alert
   */
  acknowledgeAlert: async (alertId) => {
    const response = await apiClient.post('/insights/alerts/acknowledge', {
      alert_id: alertId
    });
    return response.data;
  },

  /**
   * Resolve an alert
   * @param {string} alertId - UUID of the alert
   */
  resolveAlert: async (alertId) => {
    const response = await apiClient.post('/insights/alerts/resolve', {
      alert_id: alertId
    });
    return response.data;
  },

  // ========================================
  // CHAT ASSISTANT
  // ========================================

  /**
   * Send a message to the chat assistant
   * @param {string} message - User message
   * @param {string|null} sessionId - Existing session ID (optional)
   * @param {string} contextType - Context type (general, dashboard, alert, execution)
   */
  sendMessage: async (message, sessionId = null, contextType = 'general') => {
    const response = await apiClient.post('/insights/chat', {
      message,
      session_id: sessionId,
      context_type: contextType
    });
    return response.data;
  },

  /**
   * Get chat sessions for current user
   * @param {number} limit - Max sessions to return
   */
  getChatSessions: async (limit = 20) => {
    const response = await apiClient.get('/insights/chat/sessions', {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Get messages from a chat session
   * @param {string} sessionId - UUID of the session
   * @param {number} limit - Max messages to return
   */
  getSessionMessages: async (sessionId, limit = 50) => {
    const response = await apiClient.get(`/insights/chat/sessions/${sessionId}/messages`, {
      params: { limit }
    });
    return response.data;
  },

  /**
   * Close a chat session
   * @param {string} sessionId - UUID of the session
   */
  closeSession: async (sessionId) => {
    const response = await apiClient.post(`/insights/chat/sessions/${sessionId}/close`);
    return response.data;
  }
};

export default insightsService;
